import { formatInTimeZone } from 'date-fns-tz';
import { REMUNERACION_TIERS, RemuneracionTier, RemuneracionZone } from './remuneracionTiers';
import { calculatePaymentDueDate, getSettlementStatus, SettlementStatus } from './paymentDueDate';
import { BA_TIMEZONE } from './timezone';

/**
 * Cálculo on-the-fly de la liquidación NZ (zonas de entrada), sin persistencia:
 * se reprocesa la serie completa de sesiones (settled + lost, ver
 * NZ_ACCUMULATION_SESSION_STATUSES) por mes cada vez.
 * Reutiliza REMUNERACION_TIERS (misma tabla que consume MetricsLiquidacionTab.tsx en la UI)
 * como única fuente de verdad de rangos/montos — no se duplica la tabla acá.
 */

export interface MonthlySettledSessions {
  /** Formato 'YYYY-MM'. Se asume orden cronológico ascendente, arrancando en el primer mes
   *  del esquema NZ (julio 2026) con acumulado en cero — el caller es responsable de recortar
   *  la serie a partir de ese mes. */
  month: string;
  /** Nombre heredado ("Settled") por compatibilidad: en la práctica es settled + lost — ver
   *  NZ_ACCUMULATION_SESSION_STATUSES. Mismo criterio de acumulación que finanzas-gitlab. */
  sessionsCount: number;
}

export interface NZSettlementMonthResult {
  month: string;
  sessionsThisMonth: number;
  /** Sesiones acumuladas de meses anteriores que todavía no se liquidaron, antes de sumar este mes. */
  carriedOverFromPreviousMonths: number;
  /** carriedOverFromPreviousMonths + sessionsThisMonth (BRUTO: sin descontar anticipadas). */
  totalConsideredForThreshold: number;
  /** Sesiones de este mes ya cobradas como anticipada (informadas por finanzas-gitlab). 0 si no hubo. */
  earlyPaymentSessionsPaid: number;
  /** totalConsideredForThreshold − earlyPaymentSessionsPaid: lo que de verdad se evalúa contra la
   *  franja (esas sesiones ya se pagaron, no vuelven a contar para alcanzarla). */
  netConsideredForThreshold: number;
  /** Tier de REMUNERACION_TIERS alcanzado este mes (sobre el acumulado NETO), o null si no se llegó al umbral mínimo. */
  tier: RemuneracionTier | null;
  crossedThreshold: boolean;
  /** Monto numérico a liquidar este mes (parseado de tier.amount), null si no cruzó el umbral. */
  amountToSettle: number | null;
  /** Acumulado que pasa al mes siguiente (0 si este mes liquidó y el contador se reinició). */
  remainingCarryOver: number;
  /** Meses consecutivos (incluyendo este) sin cruzar el umbral mínimo. Se reinicia a 0 al liquidar. */
  monthsWithoutThreshold: number;
  /** Meses COMPLETOS consecutivos sin cruzar el umbral inmediatamente ANTES de este mes (no
   *  cuenta el mes en sí, que puede estar en curso). Es lo que decide la elegibilidad. */
  completedMonthsWithoutThreshold: number;
  /** true cuando los 2 meses completos anteriores a este quedaron sin cruzar el umbral — habilita
   *  pedir liquidación anticipada ("después de dos meses completos el acumulado todavía no llegó a
   *  15"; mismo criterio que finanzas-gitlab, que cuenta períodos ya cerrados). El mes en curso NO
   *  cuenta como uno de los dos. La acción de solicitarla se resuelve en la UI; acá solo es el
   *  estado calculado. */
  earlyLiquidationEligible: boolean;
  /** $50 cada 5 sesiones acumuladas, informativo — solo tiene sentido si earlyLiquidationEligible es true. */
  earlyLiquidationAmount: number;
  /** Día 5 calendario del mes siguiente, corrido al próximo día hábil si cae en fin de semana o
   *  feriado nacional argentino (ver paymentDueDate.ts). */
  dueDate: Date;
  /** 'pending' si hoy es anterior a dueDate, 'paid' si es igual o posterior (sin verificar
   *  acreditación real — simplificación aceptada para V1). */
  status: SettlementStatus;
}

/**
 * Mes de inicio del esquema NZ ('YYYY-MM'). El acumulado arranca en cero en este mes —
 * cualquier serie de sesiones armada para calculateNZSettlementSchedule debe empezar acá.
 */
export const NZ_SETTLEMENT_SCHEME_START_MONTH = '2026-07';

/**
 * Sesiones que cuentan para el acumulado de tramo del esquema range/NZ: `settled` (cobrada) y
 * `lost` (no genera revenue, pero finanzas-gitlab igual le paga la sesión a la psicóloga —
 * mismo criterio que `PsychologistRangeLedger` del lado de finanzas-gitlab, confirmado con ese
 * proyecto). Corregido tras detectar que se traían solo `settled`, subcontando el acumulado real
 * de cualquier psicóloga con sesiones `lost` en el mes.
 *
 * Deliberadamente DISTINTO de `EFFECTIVE_SESSION_STATUSES` (utils/sessions.ts, que excluye
 * `lost` a propósito) — ese gobierna métricas de adherencia/actividad, un criterio de negocio
 * distinto al de payroll. No unificar.
 */
export const NZ_ACCUMULATION_SESSION_STATUSES = 'settled,lost';

// Acá vivía `isNZPsychologist(rankingCategory)`. Se eliminó a propósito: comparar
// `rankingCategory` contra 'NZ' para decidir qué se le muestra a la psicóloga es un segundo
// criterio en paralelo al gate central (ProfileMetricsTab → journeyMode → prop `isNZ`), y es
// justo lo que arregló el commit 4e380d1. Además ignora `ranking_anterior` (recién graduadas)
// y decide con `undefined` si el contexto todavía no cargó. Si necesitás saber si es NZ,
// tomalo de la prop que baja del gate central, no de acá.

interface TierRange {
  tier: RemuneracionTier;
  min: number;
  max: number; // Infinity para el tier superior abierto ('130+')
}

const EARLY_LIQUIDATION_SESSIONS_BLOCK = 5;
const EARLY_LIQUIDATION_AMOUNT_PER_BLOCK = 50;
const MONTHS_WITHOUT_THRESHOLD_FOR_EARLY_LIQUIDATION = 2;

/** '$1.030' -> 1030, '$170' -> 170 (formato AR: '.' como separador de miles). */
function parseTierAmount(amount: string): number {
  const digitsOnly = amount.replace(/[^0-9]/g, '');
  return digitsOnly === '' ? 0 : parseInt(digitsOnly, 10);
}

/** '15 – 19' -> [15, 19], '130+' -> [130, Infinity]. */
function parseTierRange(range: string): [number, number] {
  const trimmed = range.trim();
  if (trimmed.endsWith('+')) {
    const min = parseInt(trimmed.slice(0, -1), 10);
    return [min, Infinity];
  }
  const [minStr, maxStr] = trimmed.split('–').map((s) => s.trim());
  return [parseInt(minStr, 10), parseInt(maxStr, 10)];
}

function buildTierRanges(tiers: RemuneracionTier[]): TierRange[] {
  return tiers.map((tier) => {
    const [min, max] = parseTierRange(tier.range);
    return { tier, min, max };
  });
}

const TIER_RANGES = buildTierRanges(REMUNERACION_TIERS);

/** Umbral mínimo derivado del primer tier de REMUNERACION_TIERS (hoy 15), en vez de repetir el
 *  número mágico — si la tabla de tarifas cambia, el umbral se ajusta solo. */
export const NZ_MIN_THRESHOLD = TIER_RANGES.length > 0 ? TIER_RANGES[0].min : 0;

function findTierForSessionsCount(count: number): RemuneracionTier | null {
  const match = TIER_RANGES.find(({ min, max }) => count >= min && count <= max);
  return match ? match.tier : null;
}

export function getRemuneracionZoneForSessionsCount(count: number): RemuneracionZone | null {
  return findTierForSessionsCount(count)?.zone ?? null;
}

/**
 * Procesa la serie mensual de sesiones settled de una psicóloga NZ (en orden cronológico,
 * arrancando en el mes en que el acumulado es cero) y devuelve, para cada mes: sesiones del
 * mes, acumulado arrastrado, franja/monto liquidado (con reset del contador al cruzar), y el
 * flag de elegibilidad para liquidación anticipada tras 2 meses sin cruzar el umbral mínimo.
 *
 * `earlyPaymentsByMonth` (opcional, 'YYYY-MM' -> sesiones ya pagadas como anticipada en ese
 * mes) replica lo que hace finanzas-gitlab (`computeRangeTierStatus`): la franja se busca
 * contra el acumulado NETO (`totalConsideredForThreshold − anticipada pagada ese mes`), porque
 * esas sesiones ya se cobraron y no pueden volver a pagarse dentro de una franja (si no, se
 * cobrarían dos veces: anticipada + franja). Lo mismo vale para el carryOver que pasa al mes
 * siguiente. `totalConsideredForThreshold` queda BRUTO (es lo que se muestra y lo que la
 * psicóloga vio al pedir la anticipada). Una anticipada tampoco resetea
 * `monthsWithoutThreshold` (es un adelanto a cuenta, no una liquidación completa).
 *
 * La elegibilidad para pedir la anticipada exige DOS MESES COMPLETOS sin cruzar el umbral antes
 * del mes que se mira (ver `completedMonthsWithoutThreshold`); el mes en curso no cuenta.
 *
 * Una anticipada SOLO SOLICITADA (todavía no pagada) no entra acá: sin pago no hay descuento,
 * y si el mes llega a la franja, se liquida por franja — ver getEarlyRequestStatus.
 */
export function calculateNZSettlementSchedule(
  monthlySessions: MonthlySettledSessions[],
  today: Date = new Date(),
  earlyPaymentsByMonth: Record<string, number> = {}
): NZSettlementMonthResult[] {
  let carryOver = 0;
  let monthsWithoutThreshold = 0;

  return monthlySessions.map(({ month, sessionsCount }) => {
    const totalConsideredForThreshold = carryOver + sessionsCount;

    // No debería dar negativo nunca (finanzas-gitlab valida que la anticipada no supere el
    // acumulado disponible antes de dejar pagarla), pero Math.max(0, ...) igual por las dudas
    // de que el dato llegue desfasado (ej. la anticipada se cargó antes que alguna sesión).
    const sessionsPaidAsEarlyPayment = earlyPaymentsByMonth[month] ?? 0;
    const netAfterEarlyPayment = Math.max(0, totalConsideredForThreshold - sessionsPaidAsEarlyPayment);

    // La franja se evalúa sobre el NETO: las sesiones ya cobradas como anticipada no cuentan.
    const tier = findTierForSessionsCount(netAfterEarlyPayment);
    const crossedThreshold = tier !== null;

    const amountToSettle = crossedThreshold ? parseTierAmount(tier.amount) : null;
    const remainingCarryOver = crossedThreshold ? 0 : netAfterEarlyPayment;

    // Racha de meses COMPLETOS sin cruzar, tomada ANTES de sumar este mes: el mes en curso (o el
    // que se está mirando) no es un mes completo, así que no cuenta para habilitar la anticipada.
    const completedMonthsWithoutThreshold = monthsWithoutThreshold;
    monthsWithoutThreshold = crossedThreshold ? 0 : monthsWithoutThreshold + 1;

    const earlyLiquidationEligible =
      completedMonthsWithoutThreshold >= MONTHS_WITHOUT_THRESHOLD_FOR_EARLY_LIQUIDATION;
    // Neto de lo ya cobrado como anticipada este mes, para no ofrecer de nuevo como "podés
    // pedir anticipada por $X" sesiones que ya se pagaron.
    const earlyLiquidationAmount =
      Math.floor(netAfterEarlyPayment / EARLY_LIQUIDATION_SESSIONS_BLOCK) *
      EARLY_LIQUIDATION_AMOUNT_PER_BLOCK;

    const [closedYear, closedMonthNum] = month.split('-').map(Number);
    const closedMonth = new Date(closedYear, closedMonthNum - 1, 1);
    const dueDate = calculatePaymentDueDate(closedMonth);
    const status = getSettlementStatus(dueDate, today);

    const result: NZSettlementMonthResult = {
      month,
      sessionsThisMonth: sessionsCount,
      carriedOverFromPreviousMonths: carryOver,
      totalConsideredForThreshold,
      earlyPaymentSessionsPaid: sessionsPaidAsEarlyPayment,
      netConsideredForThreshold: netAfterEarlyPayment,
      tier,
      crossedThreshold,
      amountToSettle,
      remainingCarryOver,
      monthsWithoutThreshold,
      completedMonthsWithoutThreshold,
      earlyLiquidationEligible,
      earlyLiquidationAmount,
      dueDate,
      status,
    };

    carryOver = remainingCarryOver;

    return result;
  });
}

export interface StreakSessionsView<T> {
  /** Sesiones a listar por mes de la racha: las de meses anteriores ya cubiertas por una anticipada
   *  quedan afuera (ver `getStreakSessionsView`). Todos los meses de la racha tienen clave. */
  visibleByMonth: Record<string, T[]>;
  /** Cuántas sesiones se dejaron de listar por estar ya cubiertas por una anticipada anterior. */
  hiddenCount: number;
  /** Meses 'YYYY-MM' (anteriores al que se mira) cuya anticipada cubrió esas sesiones. */
  hiddenEarlyMonths: string[];
  /** Sesiones del mes que se mira que ya van en SU PROPIA anticipada (se siguen listando). */
  thisMonthEarlyPaid: number;
  /** Total de sesiones que quedan para listar. */
  listedSessions: number;
}

/**
 * Qué sesiones listar en el detalle de "sesiones acumuladas" de un mes.
 *
 * El acumulado que se muestra ("Llevás N de 15") es NETO: descuenta las anticipadas, también las
 * de un mes anterior de la racha (el neto de ese mes es lo que pasa como arrastre al siguiente).
 * Si el detalle listara las sesiones brutas, la psicóloga vería "Llevás 1" y 11 sesiones. Por eso,
 * en los meses POSTERIORES a la anticipada, las sesiones que esa anticipada ya cubrió no se listan.
 *
 * Criterio: finanzas-gitlab informa cuántas sesiones pagó como anticipada, no cuáles, así que se
 * asume que una anticipada cubre primero las sesiones más ANTIGUAS del acumulado (el mismo orden en
 * que se arrastran). Es solo una convención de visualización: el dinero y el neto no dependen de
 * qué sesión puntual se oculte. En el mes de la propia anticipada se listan todas (hay una nota
 * que explica el neto).
 *
 * Invariante (mientras ninguna anticipada supere lo disponible):
 * listedSessions − thisMonthEarlyPaid = netConsideredForThreshold del mes que se mira.
 *
 * @param streakMonths meses de la racha, de más viejo a más nuevo (el último es el que se mira).
 * @param sessionsByMonth sesiones de cada mes, ya ordenadas por fecha ascendente.
 */
export function getStreakSessionsView<T>(
  schedule: NZSettlementMonthResult[],
  streakMonths: string[],
  sessionsByMonth: Record<string, T[]>
): StreakSessionsView<T> {
  const currentMonth = streakMonths[streakMonths.length - 1];

  let pendingToHide = 0;
  const hiddenEarlyMonths: string[] = [];
  for (const month of streakMonths) {
    if (month === currentMonth) continue;
    const paid = schedule.find((r) => r.month === month)?.earlyPaymentSessionsPaid ?? 0;
    if (paid > 0) {
      pendingToHide += paid;
      hiddenEarlyMonths.push(month);
    }
  }

  let hiddenCount = 0;
  let listedSessions = 0;
  const visibleByMonth: Record<string, T[]> = {};
  for (const month of streakMonths) {
    const sessions = sessionsByMonth[month] ?? [];
    const toDrop = Math.min(pendingToHide, sessions.length);
    pendingToHide -= toDrop;
    hiddenCount += toDrop;
    visibleByMonth[month] = sessions.slice(toDrop);
    listedSessions += visibleByMonth[month].length;
  }

  const thisMonthEarlyPaid = schedule.find((r) => r.month === currentMonth)?.earlyPaymentSessionsPaid ?? 0;
  return { visibleByMonth, hiddenCount, hiddenEarlyMonths, thisMonthEarlyPaid, listedSessions };
}

/**
 * Meses a mostrar en el detalle de sesiones acumuladas: desde el PRIMER mes de la racha que tenga
 * al menos una sesión que pondere, hasta el que se mira. Los meses iniciales sin sesiones (o cuyas
 * sesiones ya cubrió una anticipada, ver `getStreakSessionsView`) no aportan nada y son ruido: la
 * racha arranca en el último mes liquidado, pero una psicóloga que empezó en septiembre no tiene por
 * qué ver "Julio: sin sesiones registradas". Los meses vacíos que vienen DESPUÉS de uno con sesiones
 * se conservan (explican el hueco). Si ningún mes tiene sesiones, queda solo el que se mira.
 *
 * @param streakMonths meses de la racha, de más viejo a más nuevo (el último es el que se mira).
 * @param visibleByMonth `StreakSessionsView.visibleByMonth` (sesiones que de verdad se listan).
 */
export function getStreakMonthsToShow(
  streakMonths: string[],
  visibleByMonth: Record<string, unknown[]>
): string[] {
  const firstWithSessions = streakMonths.findIndex((month) => (visibleByMonth[month]?.length ?? 0) > 0);
  if (firstWithSessions === -1) return streakMonths.slice(-1);
  return streakMonths.slice(firstWithSessions);
}

/** Último día (inclusive) del mes en el que la vista por default sigue mostrando el mes
 *  anterior. Se paga a mes vencido: en los primeros días del mes la psicóloga quiere ver lo
 *  que se le abona esos días (la liquidación del mes que acaba de cerrar), no el mes en curso
 *  recién arrancando y casi sin sesiones. */
const DEFAULT_VIEW_PREVIOUS_MONTH_CUTOFF_DAY = 10;

/**
 * Mes que debe mostrarse por default al entrar a "Tus liquidaciones", antes de cualquier
 * selección manual en el dropdown: hasta el día DEFAULT_VIEW_PREVIOUS_MONTH_CUTOFF_DAY
 * (inclusive) del mes se muestra el mes inmediato anterior, si existe en `schedule`; de ahí en
 * más (o si no hay mes anterior — ej. la psicóloga arrancó el esquema NZ este mismo mes), se
 * muestra el mes en curso. El día del mes se calcula en BA_TIMEZONE, la misma zona de
 * referencia que el resto del esquema (ver monthKeyFromSessionDate en useNZSettlementSchedule
 * y calculatePaymentDueDate acá), no en la del navegador de la psicóloga.
 */
export function getDefaultVisibleMonth(
  schedule: NZSettlementMonthResult[],
  today: Date = new Date()
): string | null {
  if (schedule.length === 0) return null;

  const currentMonthResult = schedule[schedule.length - 1];
  const dayOfMonth = Number(formatInTimeZone(today, BA_TIMEZONE, 'd'));
  if (dayOfMonth > DEFAULT_VIEW_PREVIOUS_MONTH_CUTOFF_DAY) return currentMonthResult.month;

  const previousMonthResult = schedule[schedule.length - 2];
  return previousMonthResult ? previousMonthResult.month : currentMonthResult.month;
}


/** Pago anticipado ya abonado de un mes (RangeEarlyPaymentRecord, informado por finanzas-gitlab). */
export interface EarlyPaymentInfo {
  /** Total acumulado del mes (no delta). */
  sessionsPaid: number;
  amountPaid: number;
  /** ISO 8601 — última CARGA de la anticipada en finanzas-gitlab. NO es la fecha del pago real
   *  (ver `getEarlyPaymentSettlement`): no mostrarla como "fecha de pago". */
  paidAt: string;
}

/**
 * Si la anticipada de un mes ya se abonó o todavía está por abonarse.
 *
 * Ojo: `EarlyPaymentInfo.paidAt` NO es la fecha del pago real. finanzas-gitlab manda ahí la
 * fecha de la última CARGA de la anticipada (`earlyPaymentPaidAt`: cuando se registra, antes de
 * que corra Payroll), y hoy no informa cuándo se paga de verdad. Por eso este estado se deriva
 * de la fecha límite de pago del mes — día 5 del mes siguiente, corrido al primer día hábil (la
 * misma regla y la misma simplificación que `getSettlementStatus` para las liquidaciones de
 * tramo: pasada esa fecha se considera abonada, sin verificar acreditación). Si finanzas-gitlab
 * llega a informar la fecha real de pago, este es el único lugar a cambiar.
 *
 * @param month 'YYYY-MM' del período de la anticipada (no la fecha de carga).
 */
export interface EarlyPaymentSettlement {
  status: SettlementStatus;
  /** Fecha límite de pago del período (día 5 del mes siguiente o primer día hábil posterior). */
  dueDate: Date;
}

export function getEarlyPaymentSettlement(month: string, today: Date = new Date()): EarlyPaymentSettlement {
  const [year, monthNum] = month.split('-').map(Number);
  const dueDate = calculatePaymentDueDate(new Date(year, monthNum - 1, 1));
  return { status: getSettlementStatus(dueDate, today), dueDate };
}

/** Solicitud de anticipada de la propia psicóloga (AnticipadaRequest). */
export interface EarlyRequestInfo {
  requestId: string;
  /** ISO 8601. */
  requestedAt: string;
  requestedSessions: number;
  estimatedAmount: number;
  /** ISO 8601 si finanzas-gitlab la descartó ("No corresponde"); null/ausente si no. */
  dismissedAt?: string | null;
  /** Motivo de finanzas-gitlab, que la psicóloga ve tal cual. */
  dismissalReason?: string | null;
}

/**
 * En qué quedó la anticipada de un mes, para mostrarla en pantalla. Parte de dos hechos
 * independientes (la solicitud de la psicóloga y el pago que informa finanzas-gitlab) y del
 * resultado del cálculo del mes:
 * - `none`: no pidió, o su última solicitud ya fue atendida (hay un pago posterior).
 * - `dismissed`: finanzas-gitlab la descartó ("No corresponde") con un motivo que ve la psicóloga.
 *   Gana sobre pending/superseded/expired, pero NO sobre una anticipada ya abonada (`none`: el
 *   pago atendió la solicitud). Una solicitud posterior es otra fila y vuelve a evaluarse sola.
 * - `pending`: pidió y todavía no se abonó; el mes sigue abierto y sin alcanzar la franja.
 * - `superseded`: pidió y no se abonó, pero el mes alcanzó la franja → se liquida por franja,
 *   la solicitud queda sin efecto (finanzas-gitlab ya no puede cargar una anticipada ahí:
 *   exige no haber alcanzado el tramo). No hay nada que descontar porque no se pagó.
 * - `expired`: pidió y no se abonó, y ya pasó la ventana de pago (el pedido es de hace más de un
 *   mes calendario) sin franja → esas sesiones siguen acumulando. Mientras el pedido sea del mes
 *   en curso o del anterior sigue `pending`: el período abierto de finanzas-gitlab es el mes
 *   anterior al calendario y todavía puede cargar la anticipada.
 */
export type EarlyRequestStatus =
  | { kind: 'none' }
  | { kind: 'dismissed'; request: EarlyRequestInfo; reason: string; dismissedAt: string }
  | { kind: 'pending'; request: EarlyRequestInfo }
  | { kind: 'superseded'; request: EarlyRequestInfo }
  | { kind: 'expired'; request: EarlyRequestInfo };

export function getEarlyRequestStatus(
  result: Pick<NZSettlementMonthResult, 'crossedThreshold'>,
  withinPaymentWindow: boolean,
  latestRequest: EarlyRequestInfo | undefined,
  payment: EarlyPaymentInfo | undefined
): EarlyRequestStatus {
  if (!latestRequest) return { kind: 'none' };

  // Atendida: hay un pago cargado después de la solicitud (finanzas-gitlab marca `paidAt` al
  // cargar cada tanda). `payment` es el ÚLTIMO pago de cualquier mes (ver getLatestEarlyPayment):
  // el período abierto de finanzas puede ser el mes anterior al del pedido. Una solicitud
  // posterior al último pago vuelve a ser pendiente.
  const attended =
    !!payment && payment.sessionsPaid > 0 && new Date(payment.paidAt).getTime() >= new Date(latestRequest.requestedAt).getTime();
  if (attended) return { kind: 'none' };

  if (latestRequest.dismissedAt) {
    return {
      kind: 'dismissed',
      request: latestRequest,
      reason: latestRequest.dismissalReason?.trim() || 'Esta solicitud no corresponde.',
      dismissedAt: latestRequest.dismissedAt,
    };
  }

  if (result.crossedThreshold) return { kind: 'superseded', request: latestRequest };
  if (!withinPaymentWindow) return { kind: 'expired', request: latestRequest };
  return { kind: 'pending', request: latestRequest };
}

/** El último pago anticipado cargado (por `paidAt`) entre todos los meses, o undefined si no hay
 *  ninguno con sesiones. Sirve para decidir si una solicitud ya fue atendida sin importar en qué
 *  mes/período lo cargó finanzas-gitlab: su período abierto puede ser el mes anterior al mes
 *  calendario en el que la psicóloga hizo el pedido. */
export function getLatestEarlyPayment(paymentsByMonth: Record<string, EarlyPaymentInfo>): EarlyPaymentInfo | undefined {
  let latest: EarlyPaymentInfo | undefined;
  for (const payment of Object.values(paymentsByMonth)) {
    if (payment.sessionsPaid <= 0) continue;
    if (!latest || new Date(payment.paidAt).getTime() > new Date(latest.paidAt).getTime()) latest = payment;
  }
  return latest;
}

/** Índice absoluto de un mes 'YYYY-MM' (año*12 + mes) para comparar meses sin armar fechas. */
function monthIndex(month: string): number {
  const [year, monthNum] = month.split('-').map(Number);
  return year * 12 + monthNum;
}

/**
 * ¿El mes de una solicitud todavía está dentro de la ventana de pago? Es el mes en curso o el
 * inmediato anterior: el período abierto de finanzas-gitlab es el mes anterior al calendario, así
 * que una solicitud hecha el mes pasado todavía se puede abonar. Pasada esa ventana, sin pago ni
 * franja, la solicitud figura vencida.
 */
export function isWithinPaymentWindow(requestMonth: string, currentMonth: string): boolean {
  const diff = monthIndex(currentMonth) - monthIndex(requestMonth);
  return diff >= 0 && diff <= 1;
}

/** La solicitud de anticipada MÁS RECIENTE entre todos los meses (por `requestedAt`), con el mes
 *  ('YYYY-MM') en que se hizo. Sirve para mostrar su estado sin importar qué mes mira la psicóloga. */
export function getLatestEarlyRequestOverall(
  latestRequestByMonth: Record<string, EarlyRequestInfo>
): { month: string; request: EarlyRequestInfo } | undefined {
  let latest: { month: string; request: EarlyRequestInfo } | undefined;
  for (const [month, request] of Object.entries(latestRequestByMonth)) {
    if (!latest || new Date(request.requestedAt).getTime() > new Date(latest.request.requestedAt).getTime()) {
      latest = { month, request };
    }
  }
  return latest;
}
